import axios from 'axios';

const MIN_BYTES = 128 * 1024; // 128 KB

export async function uploadPhotoCapture({ sessionId, stage, poseNumber, blobOrFile, meta = {} }) {
  if (!blobOrFile) {
    return { success: false, reason: "No image file provided" };
  }

  // Pre-flight client-side size check (128 KB)
  if (blobOrFile.size < MIN_BYTES) {
    const sizeKb = (blobOrFile.size / 1024).toFixed(1);
    return {
      success: false,
      status: 'rejected',
      reason: `${sizeKb} KB is below the 128 KB minimum. Move closer to the camera or improve lighting. Upscaling is not permitted.`
    };
  }

  const formData = new FormData();
  formData.append('session_id', sessionId);
  formData.append('stage', stage);
  if (poseNumber !== undefined && poseNumber !== null) {
    formData.append('pose_number', String(poseNumber));
  }

  if (meta.capturedAt) {
    formData.append('captured_at', meta.capturedAt.toISOString());
  }
  if (meta.width) {
    formData.append('actual_width', String(meta.width));
  }
  if (meta.height) {
    formData.append('actual_height', String(meta.height));
  }

  const filename = blobOrFile.name || `${stage}_${poseNumber || '0'}.jpg`;
  formData.append('file', blobOrFile, filename);

  try {
    const resp = await axios.post('/api/capture/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const data = resp.data;
    const initialStatus = data.status;

    if (initialStatus && initialStatus !== 'pending') {
      return {
        success: initialStatus === 'accepted',
        status: initialStatus,
        photoId: data.photo_id,
        rejectionReason: data.rejection_reason,
        reviewReason: data.review_reason
      };
    }

    // Poll status endpoint
    const jobId = data.job_id || data.photo_id;
    return await pollJobStatus(jobId);

  } catch (err) {
    if (err.response) {
      const status = err.response.status;
      const respData = err.response.data || {};

      if (status === 422) {
        return {
          success: false,
          status: 'rejected',
          reason: respData.reason || "Image below 128 KB minimum resolution requirement."
        };
      }
      if (status === 409) {
        return {
          success: false,
          status: 'duplicate',
          reason: respData.reason || `Pose ${poseNumber} already submitted.`
        };
      }
      return {
        success: false,
        status: 'error',
        reason: respData.error || respData.reason || `Server returned error (${status})`
      };
    }

    return {
      success: false,
      status: 'error',
      reason: err.message || "Network error while uploading capture"
    };
  }
}

async function pollJobStatus(jobId, maxAttempts = 25, intervalMs = 600) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise(r => setTimeout(r, intervalMs));
    try {
      const resp = await axios.get(`/api/capture/status/${jobId}`);
      const data = resp.data;
      if (data && data.status && data.status !== 'pending') {
        return {
          success: data.status === 'accepted',
          status: data.status,
          photoId: data.photo_id,
          rejectionReason: data.rejection_reason,
          reviewReason: data.review_reason,
          pitch: data.pitch,
          yaw: data.yaw,
          roll: data.roll,
          hasGlasses: data.has_glasses
        };
      }
    } catch (e) {
      // Ignore intermediate network hiccups during polling
    }
  }

  return {
    success: false,
    status: 'timeout',
    reason: "Validation processing timed out. Image will be reviewed in queue."
  };
}

export async function declarePhotoEra(sessionId, photoId, era) {
  try {
    const resp = await axios.post('/api/capture/historical/declare-era', {
      session_id: sessionId,
      photo_id: photoId,
      era
    });
    return resp.data;
  } catch (err) {
    throw err.response ? err.response.data : err;
  }
}

